import type { Classroom, Day, Instructor, Section } from "@models/ISchema";
import type {
  LinkScheduleSnapshot,
  LinkScheduleSnapshotSession,
  ProposalScheduleValue,
} from "@services/types/assignment-proposal/assignmentProposalTypes";
import type { BlockRanges, EditedSession } from "../context/formData.reducer";
import type { Week } from "../context/linkData.reducer";
import {
  asPersistedSession,
  checkedWeekIds,
  persistedSessionOf,
  sameIds,
  sameTime,
} from "./sessionComparison";

type ResourcesById<T> = Record<string, T>;

interface ProposalFormStateInput {
  snapshot: LinkScheduleSnapshot | null | undefined;
  /** Sections of the link as the editor holds them (sessions already adapted). */
  sections: Section[];
  weeks: Week[];
  classroomsById: ResourcesById<Classroom>;
  instructorsById: ResourcesById<Instructor>;
  /** Id for a session the proposal has to recreate because the link no longer has it. */
  createId: () => string;
}

export interface ProposalFormState {
  savedSessions: EditedSession[];
  sessionsToCreate: EditedSession[];
  savedSessionsToCreateIds: Record<string, boolean>;
  sessionsToDelete: Record<string, EditedSession>;
  /** First session the proposal edits or creates, to select it as soon as the proposal opens. */
  firstChangedSession: EditedSession | null;
  /** A section of the proposal is gone from the link: its sessions could not be loaded. */
  hasSkippedSections: boolean;
}

const toId = (value: unknown): string => String(value ?? "");

const linkSessionsOf = (section: Section): EditedSession[] =>
  [...(section?.sessions ?? []), ...(section?.unasignedSessions ?? [])] as EditedSession[];

/** Whether the proposal leaves the schedule of a session as the link has it. */
const isSameSchedule = (schedule: ProposalScheduleValue, blocks: BlockRanges | undefined) => {
  if (schedule?.mode === "NO_SCHEDULE") {
    return (
      blocks?.selected === "blocks" &&
      Number(blocks?.blocks ?? 0) === Number(schedule.no_schedule_event_count ?? 0)
    );
  }

  return (
    blocks?.selected === "hours" &&
    String(schedule?.day ?? "") === String(blocks?.day ?? "") &&
    sameTime(schedule?.start_time, blocks?.startTime) &&
    sameTime(schedule?.end_time, blocks?.endTime)
  );
};

/** The schedule of the proposal as the form holds one: a single day per session. */
const toBlocks = (schedule: ProposalScheduleValue, isNewSession: boolean): BlockRanges => {
  if (schedule?.mode === "NO_SCHEDULE") {
    return { selected: "blocks", blocks: schedule.no_schedule_event_count };
  }

  const day = schedule?.day as Day;

  return {
    selected: "hours",
    day,
    // A session created in the form stores its day in `days` too; an edited one does not.
    days: isNewSession ? [day] : undefined,
    startTime: schedule?.start_time,
    endTime: schedule?.end_time,
  };
};

const toWeeks = (weeks: Week[], intervalIds: string[] | null | undefined): Week[] => {
  const checkedIds = new Set((intervalIds ?? []).map(toId));

  return (weeks ?? []).map(week => ({
    ...week,
    checked: checkedIds.has(toId(week.id)),
    highlight: false,
  }));
};

/**
 * A classroom or instructor of the proposal. One that cannot be resolved (it no longer exists, or
 * the user cannot see it) is kept with its id as code: dropping it would silently remove it from
 * the proposal the next time it is saved, while keeping it lets the validations report it.
 */
const toResources = <T extends { id: string }>(
  ids: string[] | null | undefined,
  byId: ResourcesById<T>,
): T[] =>
  (ids ?? []).map(id => byId?.[toId(id)] ?? ({ id: toId(id), code: toId(id) } as unknown as T));

/**
 * Whether a proposal content can be applied. It must be checked before building the editor state:
 * every link session that is missing from a snapshot becomes a deletion, so applying something
 * that is not a snapshot (a content that failed to parse, an unknown format) would mark the whole
 * link for deletion.
 */
export const isLinkScheduleSnapshot = (content: unknown): content is LinkScheduleSnapshot =>
  Array.isArray((content as LinkScheduleSnapshot)?.schedule?.sections);

/** Every classroom and instructor id a proposal mentions, to know which ones must be requested. */
export const collectSnapshotResourceIds = (
  snapshot: LinkScheduleSnapshot | null | undefined,
): { classroomIds: string[]; instructorIds: string[] } => {
  const classroomIds = new Set<string>();
  const instructorIds = new Set<string>();

  (snapshot?.schedule?.sections ?? []).forEach(section =>
    (section?.sessions ?? []).forEach(session => {
      (session?.assignment?.classroom_ids ?? []).forEach(id => classroomIds.add(toId(id)));
      (session?.assignment?.instructor_ids ?? []).forEach(id => instructorIds.add(toId(id)));
    }),
  );

  return { classroomIds: [...classroomIds], instructorIds: [...instructorIds] };
};

/** The classrooms and instructors the link's sessions already have, by id. */
export const collectLinkResources = (
  sections: Section[] | null | undefined,
): { classroomsById: ResourcesById<Classroom>; instructorsById: ResourcesById<Instructor> } => {
  const classroomsById: ResourcesById<Classroom> = {};
  const instructorsById: ResourcesById<Instructor> = {};

  (sections ?? []).flatMap(linkSessionsOf).forEach(session => {
    (session?.classrooms ?? []).forEach(classroom => {
      if (classroom?.id) classroomsById[toId(classroom.id)] = classroom;
    });
    (session?.instructors ?? []).forEach(instructor => {
      if (instructor?.id) instructorsById[toId(instructor.id)] = instructor;
    });
  });

  return { classroomsById, instructorsById };
};

/**
 * Turns a proposal (the complete schedule of the link as it would be) into the pending changes of
 * the editor, applied over the link's current data. The proposal replaces the whole schedule but
 * keeps the identity of the sessions:
 * - a snapshot session that exists in the link and differs from it is an edited session;
 * - one that equals the link's is not touched;
 * - one the proposal creates, and one whose session the link no longer has, is a session to create
 *   (the first keeps its id, the second gets a new one);
 * - a link session that is not in the snapshot is a session to delete;
 * - a snapshot section the link no longer has is left out and reported.
 */
export const buildFormStateFromProposal = ({
  snapshot,
  sections,
  weeks,
  classroomsById,
  instructorsById,
  createId,
}: ProposalFormStateInput): ProposalFormState => {
  const linkSectionsById = new Map((sections ?? []).map(section => [toId(section.id), section]));
  const linkSessions = (sections ?? []).flatMap(linkSessionsOf);
  const linkSessionsById = new Map(linkSessions.map(session => [toId(session.id), session]));

  const savedSessions: EditedSession[] = [];
  const sessionsToCreate: EditedSession[] = [];
  const keptSessionIds = new Set<string>();
  let hasSkippedSections = false;

  const proposedResources = (snapshotSession: LinkScheduleSnapshotSession) => ({
    intervals: toWeeks(weeks, snapshotSession?.assignment?.interval_ids),
    classrooms: toResources(snapshotSession?.assignment?.classroom_ids, classroomsById),
    instructors: toResources(snapshotSession?.assignment?.instructor_ids, instructorsById),
  });

  (snapshot?.schedule?.sections ?? []).forEach(snapshotSection => {
    const section = linkSectionsById.get(toId(snapshotSection?.section_id));

    if (!section) {
      hasSkippedSections ||= (snapshotSection?.sessions ?? []).length > 0;
      return;
    }

    (snapshotSection?.sessions ?? []).forEach(snapshotSession => {
      const schedule = snapshotSession?.assignment?.schedule;
      const current = linkSessionsById.get(toId(snapshotSession?.session_id));

      if (snapshotSession?.session_id && current) {
        keptSessionIds.add(toId(current.id));

        const resources = proposedResources(snapshotSession);
        const isScheduleUnmoved = isSameSchedule(schedule, current.blocks);
        const isUnchanged =
          isScheduleUnmoved &&
          sameIds(
            resources.classrooms.map(item => item.id),
            (current.classrooms ?? []).map(item => item.id),
          ) &&
          sameIds(
            resources.instructors.map(item => item.id),
            (current.instructors ?? []).map(item => item.id),
          ) &&
          sameIds(checkedWeekIds(resources.intervals), checkedWeekIds(current.intervals));

        if (isUnchanged) return;

        savedSessions.push({
          id: current.id,
          // A schedule the proposal does not move stays exactly as the link has it.
          blocks: isScheduleUnmoved ? current.blocks : toBlocks(schedule, false),
          ...resources,
          section: current.section,
          session: asPersistedSession(current),
        });
        return;
      }

      // Created by the proposal, or an existing session the link lost since: both are creates.
      sessionsToCreate.push({
        id: snapshotSession?.created_session_id || createId(),
        blocks: toBlocks(schedule, true),
        ...proposedResources(snapshotSession),
        session: null,
        isNew: true,
        isCloned: false,
        section: structuredClone(section),
      });
    });
  });

  const sessionsToDelete: Record<string, EditedSession> = Object.fromEntries(
    linkSessions
      .filter(session => !keptSessionIds.has(toId(session.id)))
      .map(session => [toId(session.id), session]),
  );

  return {
    savedSessions,
    sessionsToCreate,
    // Mirrors what saving an edition in the form records for the edited existing sessions.
    savedSessionsToCreateIds: Object.fromEntries(savedSessions.map(session => [session.id, true])),
    sessionsToDelete,
    // An existing session is selected through the link's own object, as the sessions list does.
    firstChangedSession: persistedSessionOf(savedSessions[0]) ?? sessionsToCreate[0] ?? null,
    hasSkippedSections,
  };
};
